import React, { useMemo } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SM, UI_TAG } from "oolib/dist/v2/components/Typo2";
import { Hash } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { useLensesV2Context } from "../LensesV2Context";
import { useTagCooccurrence } from "@/components/analysisModules/AnnotationExplorer/comps/AnnotationExplorerVizPanel/queries/useTagCooccurrence";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { TrendPill } from "../comps/TrendPill";
import { SIGNAL_TOPIC_SIDES, toTagCountMap, COOCCURRENCE_FULL } from "../utils";
import { assembleTopics } from "./utils";
import { CONFIG } from "./CONFIG";

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopicRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${colors2.grey5};

  &:last-child {
    border-bottom: none;
  }
`;

const TopicNumber = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors2.grey10};
  color: ${colors2.grey70};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const TopicLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

const SignalChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.6rem;
`;

const SignalChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  background: ${colors2.grey5};
  color: ${colors2.grey60};
`;

const TopicRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

// Topics widget — per topic: count, period-over-period trend, top-2 co-occurring
// signals. Same v1 list UI (numbered rows, signal chips, count, trend pill),
// minus the drilldown sidebar. Rides on the SAME cooccurrence + prev-count
// queries the Signals widget makes (identical args → shared react-query cache).
export const TopicsWidget = () => {
  const { findQuery, prevFindQuery, topics: topicList } = useLensesV2Context();

  const cooccurrence = useTagCooccurrence({
    sides: SIGNAL_TOPIC_SIDES,
    ...COOCCURRENCE_FULL, // full unfiltered set — no top-N slice
    findQuery,
    pinSideBAsTagB: true,
    enabled: !!findQuery,
  });

  const prevCounts = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: ["annotations.tags.tags"],
    findQuery: prevFindQuery,
    vizCardId: "lensV2PrevTagCounts",
    queryOptions: { enabled: !!prevFindQuery },
  });

  const topics = useMemo(
    () => assembleTopics(topicList, cooccurrence.data, toTagCountMap(prevCounts.data)),
    [topicList, cooccurrence.data, prevCounts.data]
  );

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <Hash size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Topics</UI_BODY_SEMIBOLD_SM_DF>
        </div>
      }
      style={{ gap: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: 520 }}
    >
      {cooccurrence.isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0" }}>
              <SkeletonLoader style={{ height: 14, width: `${50 + i * 5}%`, borderRadius: 4 }} />
              <SkeletonLoader style={{ height: 22, width: 60, borderRadius: 12 }} />
            </div>
          ))}
        </div>
      )}

      {cooccurrence.isError && (
        <UI_BODY_SM style={{ color: "#dc2626" }}>
          Error: {cooccurrence.error?.response?.data?.message || cooccurrence.error?.message}
        </UI_BODY_SM>
      )}

      {cooccurrence.data && topics.length === 0 && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No topic data available yet.</UI_BODY_SM>
      )}

      {cooccurrence.data && topics.length > 0 && (
        <TopicList>
          {topics.slice(0, CONFIG.maxTopics).map((topic, idx) => (
            <TopicRow key={topic.topicId}>
              <TopicNumber>
                <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey70 }}>{idx + 1}</UI_BODY_SEMIBOLD_SM_DF>
              </TopicNumber>
              <TopicLabel>
                <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>{topic.topic}</UI_BODY_SEMIBOLD_SM_DF>
                {topic.signals?.length > 0 && (
                  <SignalChipsRow>
                    {topic.signals.map((sig) => (
                      <SignalChip key={sig.signalId}>
                        <UI_TAG>{sig.signal}</UI_TAG>
                      </SignalChip>
                    ))}
                  </SignalChipsRow>
                )}
              </TopicLabel>
              <TopicRight>
                <div style={{ minWidth: 60, display: "flex", justifyContent: "center" }}>
                  <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey60 }}>{topic.total}</UI_BODY_SEMIBOLD_SM_DF>
                </div>
                <div style={{ minWidth: 100, display: "flex", justifyContent: "flex-end" }}>
                  <TrendPill trend={topic.trend} />
                </div>
              </TopicRight>
            </TopicRow>
          ))}
        </TopicList>
      )}
    </DashboardWidgetShell>
  );
};
